import router from '@/router'
import { TrainingService } from '@/services/training.service'
import { BTrainingService } from '@/services/btraining.service'
import { StatService } from '@/services/stat.service'
import { HunterService } from '@/services/hunter.service'
import { UserService } from '@/services/user.service'
import { HTTP_STATUS_CODES } from '@/configs/constants.ts'
import { commitPaginationFromMeta, showErrorToast } from './helpers'
import TokenService from '@/services/storage.service'
import { normalizeQuestions } from '@/helpers/questionNormalization'

export const trainingActions = {
  async loadTrainings({ commit }, page) {
    const response = await TrainingService.trainings(page)
    commit('SET_TRAININGS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadBTrainings({ commit }, page) {
    const response = await BTrainingService.trainings(page)
    commit('SET_B_TRAININGS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadTrainingsResults({ commit }, data) {
    const response = await StatService.trainingResults(data)
    commit('SET_TRAINING_RESULTS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadBTrainingsResults({ commit }, page) {
    const response = await StatService.bTrainingResults(page)
    commit('SET_B_TRAINING_RESULTS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadExternalTrainingsResults({ commit }, page) {
    const response = await StatService.externalTrainingResults(page)
    commit('SET_EXTERNAL_TRAINING_RESULTS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadInvestmentsResults({ commit }, page) {
    const response = await StatService.investmentResults(page)
    commit('SET_EXTERNAL_TRAINING_RESULTS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadHunterResults({ commit }, page) {
    const response = await StatService.hunterResults(page)
    commit('SET_HUNTER_RESULTS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadUberallResults({ commit }, page) {
    const response = await StatService.uberallResults(page)
    commit('SET_UBERALL_RESULTS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadTrainingById({ commit }, id) {
    const response = await TrainingService.training(id)
    commit('SET_TRAINING', response.data.data)
    commit('SET_QUESTIONS', normalizeQuestions(response.data.data.questions || [], TokenService.getLangs()))
    commit('SET_CERTIFICATE_TEXTS', response.data.data.certificates)
  },

  async loadBTrainingById({ commit }, id) {
    const response = await BTrainingService.training(id)
    commit('SET_B_TRAINING', response.data.data)
    commit('SET_B_TRAINING_TYPES', response.data.data.allTypes)
    commit('SET_B_TRAINING_COSTS', response.data.data.allCosts)
  },

  async loadHunterDetails({ commit }, page) {
    const response = await HunterService.hunterDetails(page)
    commit('SET_HUNTER_DETAILS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadHunterDetailById({ commit }, id) {
    const response = await HunterService.hunterDetail(id)
    commit('SET_HUNTER_DETAIL', response.data.data)
  },

  async newTraining(_, data) {
    try {
      const response = await TrainingService.newTraining({
        ...data
      })

      if (response.status === HTTP_STATUS_CODES.OK) {
        router.push({
          name: 'training',
          params: { id: response.data.training_id }
        })
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async updateTraining(_, data) {
    try {
      const response = await TrainingService.updateTraining(data, data.id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async newBTraining(_, data) {
    try {
      const response = await BTrainingService.newTraining({
        ...data
      })

      if (response.status === HTTP_STATUS_CODES.OK) {
        router.push({
          name: 'btraining',
          params: { id: response.data.btraining_id }
        })
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async updateBTraining(_, data) {
    try {
      const response = await BTrainingService.updateTraining(data, data.id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async deleteBTrainingDetail(_, id) {
    try {
      const response = await BTrainingService.deleteBTrainingDetailById(id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadBTrainingTypes({ commit }) {
    const response = await BTrainingService.bTrainingTypes()
    commit('SET_B_TRAINING_TYPES', response.data.data)
  },

  async newBTrainingDetails(_, data) {
    try {
      const response = await BTrainingService.newBTrainingDetail(data)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadBTrainingDetails({ commit }, trainingId) {
    const response = await BTrainingService.bTrainingDetails(trainingId)
    commit('SET_B_TRAINING_DETAILS', response.data.data)
  },

  async loadQuestions({ commit }, trainingId) {
    try {
      const response = await UserService.questions(trainingId)
      const payload = Array.isArray(response.data)
        ? response.data
        : response.data?.data?.questions || response.data?.data || response.data?.questions || []
      commit('SET_QUESTIONS', normalizeQuestions(payload, TokenService.getLangs()))
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async newQuestion(_, data) {
    try {
      const response = await UserService.newQuestion({
        ...data
      })

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async deleteQuestion(_, id) {
    try {
      const response = await UserService.deleteQuestionById(id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async updateQuestion(_, data) {
    try {
      const questionId = data?.plainId ?? data?.id
      const response = await UserService.updateQuestion(questionId, data)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async orderQuestion(_, data) {
    try {
      const response = await UserService.reorderQuestion({
        ...data
      })

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadCertificateTexts({ commit }, id) {
    try {
      const response = await TrainingService.certificateTexts(id)

      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_CERTIFICATE_TEXTS', response.data.data)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  }
}
